/**
 * Files.tsx — drag-drop file transfer over the SPEC §9 `files` data channel,
 * implementing §9.2 length-prefixed binary framing + bufferedAmount backpressure.
 *
 * Upload (tech→agent): offer → wait accept → chunk* (with backpressure) →
 * complete. Download (agent→tech): the agent sends an `offer`; we `accept`,
 * assemble chunks by offset, verify SHA-256, and present a blob URL.
 *
 * Chunk payloads MAY be E2EE-sealed (SPEC §10); the framing layer is agnostic.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '@/api/provider';
import { startSession, type ActiveSession } from '@/lib/session';
import { ConsentBadge } from '@/components/ConsentBadge';
import { useToast } from '@/components/Toast';
import { uploadFile, frame, deframe } from '@/lib/filetransfer';
import { sealData, openDataChecked } from '@/lib/e2ee';
import { sha256Hex, toArrayBuffer } from '@/lib/binary';
import { CHANNELS, type ConsentData, type FileHeader } from '@/types';

interface TransferRow {
  id: string;
  name: string;
  size: number;
  sent: number;
  dir: 'up' | 'down';
  state: 'active' | 'done' | 'error';
  message?: string;
  url?: string;
}

export default function Files() {
  const { deviceId = '' } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const toast = useToast();

  const sessionRef = useRef<ActiveSession | null>(null);
  const filesChanRef = useRef<RTCDataChannel | null>(null);
  const [status, setStatus] = useState('connecting');
  const [dragOver, setDragOver] = useState(false);
  const [rows, setRows] = useState<TransferRow[]>([]);
  const [consent, setConsent] = useState<boolean | null>(null);
  const [remoteUser, setRemoteUser] = useState<string | undefined>();

  // Incoming-download assembly buffers keyed by transfer_id.
  const incoming = useRef<Map<string, { meta: Extract<FileHeader, { kind: 'offer' }>; chunks: Uint8Array[]; received: number }>>(new Map());

  function upsert(row: TransferRow) {
    setRows((rs) => {
      const i = rs.findIndex((r) => r.id === row.id);
      if (i === -1) return [row, ...rs];
      const copy = rs.slice();
      copy[i] = row;
      return copy;
    });
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await startSession(api, deviceId, 'control', {
          onTrack: () => {},
          onChannel: (label, ch) => {
            if (label !== CHANNELS.files) return;
            ch.binaryType = 'arraybuffer';
            filesChanRef.current = ch;
            setStatus('connected');
            ch.addEventListener('message', (ev) => onFilesMessage(ev, ch));
          },
          onControl: (msg) => {
            if (msg.kind === 'consent') {
              const c = msg.data as ConsentData | undefined;
              if (c) {
                setConsent(c.granted);
                setRemoteUser(c.user);
                if (!c.granted) toast.warn('End user denied consent — transfer session must end.');
              }
            }
          },
          onSecure: () => setStatus('secure'),
          onConnState: (s) => {
            if (s === 'reconnecting') {
              setStatus('reconnecting');
              toast.warn('File channel dropped — reconnecting…', { ttlMs: 4000 });
            }
          },
          onError: () => {
            setStatus('error');
            toast.error('File transfer session error.');
          },
          onEnded: () => setStatus('ended'),
        });
        if (cancelled) {
          session.end();
          return;
        }
        sessionRef.current = session;
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      sessionRef.current?.end();
      sessionRef.current = null;
    };
    // onFilesMessage only reads refs + stable setters; the session is bound once
    // per (api, deviceId). Including it would needlessly re-establish the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, deviceId]);

  // E2EE the WHOLE §9.2 frame (header+chunk), matching the agent: the relay sees
  // ciphertext only, and headers ride INSIDE the envelope. Both fail CLOSED when
  // E2EE is not yet established — never send/accept file frames in cleartext.
  function sealFrame(frameBytes: Uint8Array): Uint8Array {
    const keys = sessionRef.current?.sessionKeys() ?? null;
    if (!keys) throw new Error('E2EE not established — refusing to send file frame in cleartext');
    return sealData(keys, frameBytes);
  }
  function openFrame(payload: Uint8Array): Uint8Array {
    const keys = sessionRef.current?.sessionKeys() ?? null;
    if (!keys) throw new Error('E2EE not established — refusing to open unencrypted file frame');
    return openDataChecked(keys, payload); // throws on replayed/stale/undecryptable payload
  }
  // Seal a reply frame (accept/ack/complete) and write it to the channel.
  function sendFrame(ch: RTCDataChannel, header: FileHeader) {
    ch.send(toArrayBuffer(sealFrame(frame(header))));
  }

  async function onFilesMessage(ev: MessageEvent, ch: RTCDataChannel) {
    if (!(ev.data instanceof ArrayBuffer)) return;
    // OPEN the whole payload FIRST, then deframe (agent-identical order). Sealing
    // is whole-frame, so the header is only readable after a successful open.
    let header: FileHeader;
    let chunk: Uint8Array;
    try {
      ({ header, chunk } = deframe(openFrame(new Uint8Array(ev.data))));
    } catch {
      return; // undecryptable / replayed / not a frame for us → drop
    }

    switch (header.kind) {
      case 'offer': {
        // Agent → tech download. Accept and begin assembly.
        incoming.current.set(header.transfer_id, { meta: header, chunks: [], received: 0 });
        upsert({ id: header.transfer_id, name: header.name, size: header.size, sent: 0, dir: 'down', state: 'active' });
        sendFrame(ch, { kind: 'accept', transfer_id: header.transfer_id });
        break;
      }
      case 'chunk': {
        const entry = incoming.current.get(header.transfer_id);
        if (!entry) return;
        // chunk bytes are already cleartext (the whole frame was opened above).
        // Copy out of the aliased view so the assembled blob is independent.
        const clear = chunk.slice();
        entry.chunks.push(clear);
        entry.received += clear.length;
        sendFrame(ch, { kind: 'ack', transfer_id: header.transfer_id, seq: header.seq });
        upsert({
          id: header.transfer_id,
          name: entry.meta.name,
          size: entry.meta.size,
          sent: entry.received,
          dir: 'down',
          state: 'active',
        });
        break;
      }
      case 'complete': {
        const entry = incoming.current.get(header.transfer_id);
        if (!entry) return;
        const blob = concat(entry.chunks);
        const digest = await sha256Hex(blob);
        const ok = digest === entry.meta.sha256;
        const url = ok ? URL.createObjectURL(new Blob([blob.buffer as ArrayBuffer])) : undefined;
        upsert({
          id: header.transfer_id,
          name: entry.meta.name,
          size: entry.meta.size,
          sent: entry.received,
          dir: 'down',
          state: ok ? 'done' : 'error',
          message: ok ? undefined : 'integrity check failed',
          ...(url ? { url } : {}),
        });
        sendFrame(ch, { kind: 'complete', transfer_id: header.transfer_id });
        if (ok) toast.success(`Downloaded ${entry.meta.name}`, { ttlMs: 4000 });
        else toast.error(`Integrity check failed for ${entry.meta.name}`);
        incoming.current.delete(header.transfer_id);
        break;
      }
      case 'error': {
        upsert({ id: header.transfer_id, name: header.transfer_id, size: 0, sent: 0, dir: 'down', state: 'error', message: header.message });
        incoming.current.delete(header.transfer_id);
        break;
      }
      default:
        break;
    }
  }

  async function sendFiles(fileList: FileList | File[]) {
    const ch = filesChanRef.current;
    if (!ch || ch.readyState !== 'open') return;
    for (const file of Array.from(fileList)) {
      const id = crypto.randomUUID();
      upsert({ id, name: file.name, size: file.size, sent: 0, dir: 'up', state: 'active' });
      try {
        await uploadFile(ch, file, {
          transferId: id,
          onProgress: (sent, total) =>
            upsert({ id, name: file.name, size: total, sent, dir: 'up', state: 'active' }),
          // E2EE-seal each WHOLE frame (header+chunk) before it hits the wire and
          // open whole inbound frames (SPEC §10). Both fail closed if E2EE isn't
          // established — never upload/accept file bytes in cleartext.
          seal: sealFrame,
          open: openFrame,
        });
        upsert({ id, name: file.name, size: file.size, sent: file.size, dir: 'up', state: 'done' });
        toast.success(`Uploaded ${file.name}`, { ttlMs: 4000 });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'upload failed';
        upsert({
          id,
          name: file.name,
          size: file.size,
          sent: 0,
          dir: 'up',
          state: 'error',
          message,
        });
        toast.error(`Upload failed: ${file.name} — ${message}`);
      }
    }
  }

  function disconnect() {
    sessionRef.current?.end();
    navigate(-1);
  }

  return (
    <div className="flex h-screen flex-col bg-helm-bg">
      <div className="flex items-center gap-3 border-b border-helm-border bg-helm-panel px-4 py-2.5">
        <span className="text-sm font-semibold text-helm-text">File Transfer</span>
        <span className={`rh-badge ${statusBadge(status)}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${statusDot(status)}`} />
          {status}
        </span>
        {/* Consent/presence badge for the entire transfer session (SPEC §11.7). */}
        <ConsentBadge granted={consent} remoteUser={remoteUser} onDisconnect={disconnect} />
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rh-btn-ghost ml-auto px-2.5 py-1 text-xs"
        >
          Close
        </button>
      </div>

      <div className="flex-1 overflow-auto p-6 sm:p-8">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            void sendFiles(e.dataTransfer.files);
          }}
          className={`mb-6 grid h-44 place-items-center rounded-xl border-2 border-dashed text-sm transition ${
            dragOver ? 'border-helm-accent bg-helm-accent/10 text-helm-text' : 'border-helm-border text-helm-muted'
          }`}
        >
          <div className="text-center">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mx-auto mb-2 h-7 w-7 text-helm-muted"
              aria-hidden="true"
            >
              <path d="M12 16V4" />
              <path d="m8 8 4-4 4 4" />
              <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
            </svg>
            <p className="font-medium text-helm-text">Drag files here to upload to the device</p>
            <p className="mt-0.5 text-xs text-helm-muted">or pick them manually</p>
            <label className="rh-btn-primary mt-3 inline-flex cursor-pointer px-3 py-1.5 text-xs">
              Choose files
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => e.target.files && void sendFiles(e.target.files)}
              />
            </label>
          </div>
        </div>

        <div className="rh-card overflow-hidden">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Direction</th>
                <th>Name</th>
                <th>Progress</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="text-helm-muted">{r.dir === 'up' ? '↑ Upload' : '↓ Download'}</td>
                  <td className="font-medium text-helm-text">
                    {r.url ? (
                      <a href={r.url} download={r.name} className="text-helm-accent hover:underline">
                        {r.name}
                      </a>
                    ) : (
                      r.name
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-40 overflow-hidden rounded-full bg-helm-elevated">
                        <div
                          className={`h-full ${r.state === 'error' ? 'bg-helm-bad' : r.state === 'done' ? 'bg-helm-good' : 'bg-helm-accent'}`}
                          style={{ width: `${r.size ? Math.min(100, (r.sent / r.size) * 100) : 0}%` }}
                        />
                      </div>
                      <span className="tabular-nums text-xs text-helm-muted">
                        {r.size ? Math.min(100, Math.round((r.sent / r.size) * 100)) : 0}%
                      </span>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`rh-badge ${
                        r.state === 'error'
                          ? 'bg-helm-bad/15 text-helm-bad'
                          : r.state === 'done'
                          ? 'bg-helm-good/15 text-helm-good'
                          : 'bg-helm-elevated text-helm-muted'
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          r.state === 'error' ? 'bg-helm-bad' : r.state === 'done' ? 'bg-helm-good' : 'bg-helm-muted'
                        }`}
                      />
                      {r.state}
                    </span>
                    {r.message ? <span className="ml-1.5 text-xs text-helm-muted">{r.message}</span> : ''}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-helm-muted">
                    No transfers yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function statusBadge(status: string): string {
  if (status === 'secure' || status === 'connected') return 'bg-helm-good/15 text-helm-good';
  if (status === 'error' || status === 'ended') return 'bg-helm-bad/15 text-helm-bad';
  if (status === 'reconnecting' || status === 'connecting') return 'bg-helm-warn/15 text-helm-warn';
  return 'bg-helm-elevated text-helm-muted';
}

function statusDot(status: string): string {
  if (status === 'secure' || status === 'connected') return 'bg-helm-good';
  if (status === 'error' || status === 'ended') return 'bg-helm-bad';
  if (status === 'reconnecting' || status === 'connecting') return 'bg-helm-warn';
  return 'bg-helm-muted';
}

function concat(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}
